// src/pages/CapAccessManager.jsx

import React, { useEffect, useState, useRef } from 'react';
import { Table, DropdownButton, MenuItem, PageHeader, Button, ButtonGroup } from 'react-bootstrap';
import { getUser, toProperCase, getUserInformation } from '../utils/Common';
import { 
  getBuilder, 
  getCapsByBuilder, 
  getUsersByBuilder, 
  getUserCapsByBuilderForOwners, 
  delSharing, 
  patchSharing, 
  shareCapWithUser, 
  patchUser, 
  getUsersByDomain, 
  getUsersBySharedOnCapsByBuilder, 
  getSharedCaps, 
  getUserActions,
  recordUserAction,
  createCustomerPortal
} from '../utils/bedrockAPI';
import { CopyToClipboard } from 'react-copy-to-clipboard';
import { useHistory } from 'react-router-dom';
import "./AccessControl.css";

// Import components
import LoadingSpinner from '../components/access-control/LoadingSpinner';
import UserTable from '../components/access-control/UserTable';
import PotentialUsersTable from '../components/access-control/PotentialUsersTable';
import SharedUsersTable from '../components/access-control/SharedUsersTable';
import { getAccessColor, getReadableAccessLevel } from '../components/access-control/utils';
import AddUserModal from '../components/access-control/AddUserModal';
import AccessLevelDescriptions from '../components/access-control/AccessLevelDescriptions';

export default function AccessControl(props) {
  const history = useHistory();
  const [builder, setBuilder] = useState(null);
  const [caps, setCaps] = useState([]);
  const [users, setUsers] = useState([]);
  const [userCaps, setUserCaps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentUser, setCurrentUser] = useState(getUser());
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [potentialUsers, setPotentialUsers] = useState([]);
  const [loadingPotentialUsers, setLoadingPotentialUsers] = useState(true);
  const [moreUsers, setMoreUsers] = useState([]);
  const [allSharedUsers, setAllSharedUsers] = useState([]);
  const [copied, setCopied] = useState(false);
  const componentRef = useRef(true);
  const [loadingMoreUsers, setLoadingMoreUsers] = useState(true);
  const [showAccessInfo, setShowAccessInfo] = useState(false);
  const [showAddUserModal, setShowAddUserModal] = useState(false);
  const adminAccessFixedRef = useRef(false);

  const onCopy = () => {
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };

    window.addEventListener('resize', handleResize);
    return () => {
      componentRef.current = false;
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  useEffect(() => {
    let isMounted = true;
    async function loadBuilder(builderId) {
      const tempBuilder = await getBuilder(builderId);
      if(isMounted) {
        setBuilder(tempBuilder);
      }
    }
    if(props.match.params.builderid) {
      loadBuilder(props.match.params.builderid);
    }
    recordUserAction(getUser().userId, "access control", null, {'userData': getUserInformation()});
    return () => isMounted = false;
  },[props.match.params.builderid]);

  useEffect(() => {
    let isMounted = true;
    async function loadCaps(builderId) {
      let tempCaps = await getCapsByBuilder(builderId);
      if(isMounted) {
        tempCaps.sort((a,b) => a.name < b.name ? -1 : 1);
        setCaps(tempCaps);
      }
    }
    async function loadUsers(builderId) {
      let tempUsers = await getUsersByBuilder(builderId);
      if(isMounted) {
        // Filter out users marked for deletion -- this should be filtered at the api but just in case
        tempUsers = tempUsers.filter(user => user.builderStatus !== 'DELETE');

        tempUsers.sort((a,b) => a.email < b.email ? -1 : 1);
        setUsers(tempUsers);
      }
      if(getUser().role==='ADMIN') {
        let found = false;
        for(var i = 0; i < tempUsers.length; i++) {
          if(tempUsers[i].builderStatus === 'ADMIN') {
            setCurrentUser(tempUsers[i]);
            found = true;
            break;
          }
        }
        if(!found) {
          if(tempUsers.length > 0) {
            setCurrentUser(tempUsers[0]);
          }
          // Load shared users and their last login info
          const sharedUsers = await getUsersBySharedOnCapsByBuilder(builderId);
          let tempAllSharedUsers = [...sharedUsers];
          for (var i = 0; i < tempAllSharedUsers.length; i++) {
            const oneMonthAgo = new Date(Date.now() - 1000 * 60 * 60 * 24 * 30);
            const actions = await getUserActions(tempAllSharedUsers[i].userId, oneMonthAgo);
            tempAllSharedUsers[i].lastLogin = actions.length > 0 ? actions[0].actionDate : null;
          }
          if(isMounted) {
            setAllSharedUsers(tempAllSharedUsers);
          }
        }
      }
    }
    async function loadUserCaps(builderId) {
      const tempUserCaps = await getUserCapsByBuilderForOwners(builderId);
      if(isMounted) {
        setUserCaps(tempUserCaps);
        setLoading(false);
      }
    }
    if(builder) {
      loadCaps(builder.builderId);
      loadUsers(builder.builderId);
      loadUserCaps(builder.builderId);
    }
    return () => isMounted = false;
  },[builder]);

  async function loadPotentialUsers() {
    if (!currentUser || !currentUser.email || !users.length) return;
    
    const domain = currentUser.email.split('@')[1];
    try {
      let domainUsers = [];
      if(!['gmail.com','yahoo.com','hotmail.com','outlook.com'].includes(domain) && getUser().role==="ADMIN") {
        domainUsers = await getUsersByDomain(domain);
        // Filter out users marked for deletion
        domainUsers = domainUsers.filter(user => user.builderStatus !== 'DELETE');
        // Add last login data for domain users
        for(var i = 0; i < domainUsers.length; i++) {
          const extraUserCaps = await getSharedCaps(domainUsers[i].userId);
          domainUsers[i].caps = extraUserCaps;
          const oneMonthAgo = new Date(Date.now() - 1000 * 60 * 60 * 24 * 30);
          const actions = await getUserActions(domainUsers[i].userId, oneMonthAgo);
          domainUsers[i].lastLogin = actions.length > 0 ? actions[0].actionDate : null;
        }
      }
      // Filter out users that are already associated with this builder
      const existingUserIds = users.map(u => u.userId);
      const filteredUsers = domainUsers.filter(user => 
        !existingUserIds.includes(user.userId)
      );
      if(componentRef.current) {
        setPotentialUsers(filteredUsers);
      }
      const sharedUsers = await getUsersBySharedOnCapsByBuilder(builder.builderId);
      // Filter out users marked for deletion
      let extraUsers = sharedUsers.filter(user => 
        !existingUserIds.includes(user.userId) && 
        user.builderStatus !== 'DELETE'
      );
      extraUsers = extraUsers.filter(user => !filteredUsers.some(fu => fu.userId === user.userId));
      for(var i = 0; i < extraUsers.length; i++) {
        const extaUserCaps = await getSharedCaps(extraUsers[i].userId);
        extraUsers[i].caps = extaUserCaps;
        // Add last login data
        const oneMonthAgo = new Date(Date.now() - 1000 * 60 * 60 * 24 * 30);
        const actions = await getUserActions(extraUsers[i].userId, oneMonthAgo);
        extraUsers[i].lastLogin = actions.length > 0 ? actions[0].actionDate : null;
      }
      // Filter out users who only have SALES_DEMO access
      if (getUser().role !== 'ADMIN') {
        extraUsers = extraUsers.filter(user => {
          const hasNonDemoAccess = user.caps.some(cap => cap.accessType !== 'SALES_DEMO');
          return hasNonDemoAccess;
        });
      }
      if(componentRef.current) {
        setMoreUsers(extraUsers);
        setLoadingMoreUsers(false);
      }
    } catch (error) {
      console.error('Error loading potential users:', error);
    } finally {
      if(componentRef.current) {
        setLoadingPotentialUsers(false);
      }
    }
  }

  useEffect(() => {
    async function fixBuilderAdminAccess() {
      // Skip if we've already fixed access for this user
      if (adminAccessFixedRef.current) {
        return;
      }

      // Skip if we don't have all required data
      if (!currentUser || !currentUser.builderStatus === 'ADMIN' || caps.length === 0 || userCaps.length === 0) {
        return;
      }

      // Check if we need to fix any caps
      const needsFixing = caps.some(cap => 
        cap.status === 'ACTIVE' && 
        !userCaps.some(uc => 
          uc.userId === currentUser.userId && 
          uc.capId === cap.capId && 
          uc.accessType === 'OWNER'
        )
      );

      if (!needsFixing) {
        adminAccessFixedRef.current = true;
        return;
      }

      // Fix access for each cap that needs it
      for(var i = 0; i < caps.length; i++) {
        if(caps[i].status === 'ACTIVE') {
          const existingAccess = userCaps.find(uc => 
            uc.userId === currentUser.userId && 
            uc.capId === caps[i].capId
          );
          
          if (!existingAccess || existingAccess.accessType !== 'OWNER') {
            await handleAccessChange(currentUser.userId, caps[i].capId, 'OWNER');
          }
        }
      }
      adminAccessFixedRef.current = true;
    }

    if (currentUser && builder && currentUser.role !== 'ADMIN') {
      loadPotentialUsers();
    }
    
    fixBuilderAdminAccess();
  }, [currentUser, builder, users, caps, userCaps]);

  // Reset the ref when currentUser changes
  useEffect(() => {
    adminAccessFixedRef.current = false;
  }, [currentUser]);

  // Handler for changing access
  const handleAccessChange = async (userId, capId, newType) => {
    let userCap = userCaps.find(uc => uc.userId === userId && uc.capId === capId);
    if(newType === 'No Access') {
      if (userCap) {
        await delSharing(userCap.userCapId);
        setUserCaps(prevCaps => {
          const newCaps = prevCaps.filter(uc => uc.userId !== userId && uc.capId !== capId);
          if (currentUser?.builderStatus === 'ADMIN') {
            adminAccessFixedRef.current = true;
          }
          return newCaps;
        });
      }
    } else if(userCap && newType !== userCap.accessType) {
      const updatedUserCap = { ...userCap, accessType: newType };
      try {
        await patchSharing(userCap.userCapId, updatedUserCap);
        setUserCaps(prevCaps => {
          const newCaps = prevCaps.map(uc => uc.userCapId === userCap.userCapId ? updatedUserCap : uc);
          if (currentUser?.builderStatus === 'ADMIN') {
            adminAccessFixedRef.current = true;
          }
          return newCaps;
        });
      } catch (error) {
        console.error('Error updating access:', error);
      }
    } else if(!userCap) {
      try {
        let newUserCap = await shareCapWithUser(capId, userId, newType);
        setUserCaps(prevCaps => {
          const newCaps = [...prevCaps, newUserCap.data];
          if (currentUser?.builderStatus === 'ADMIN') {
            adminAccessFixedRef.current = true;
          }
          return newCaps;
        });
      } catch (error) {
        console.error('Error creating new access:', error);
      }
    }
  };

  const lookupUser = (userId) => {
    let user = users.find(u => u.userId == userId);
    return user;
  }

  if (loading) return <LoadingSpinner />;

  const handleUserClick = (userId) => {
    history.push(`/accesscontrol/${props.match.params.builderid}/user/${userId}`);
  };

  const handleAddUserToBuilder = async (userId) => {
    try {
      await patchUser(userId, {builderId: builder.builderId});
      // Refresh the users list
      const updatedUsers = await getUsersByBuilder(builder.builderId);
      setUsers(updatedUsers);
      
      // Remove the user from potential users
      setPotentialUsers(prev => prev.filter(u => u.userId !== userId));
    } catch (error) {
      console.error('Error adding user to builder:', error);
    }
  };

  const removeAllBuilderAccess = async (badUser) => {
    
    if(window.confirm('Are you sure you want to remove all access from this user?')) {
      if(badUser.caps) {
        for(var i = 0; i < caps.length; i++) {
          const matchingCaps = badUser.caps.filter(cap => cap.capId === caps[i].capId);
          for(var j = 0; j < matchingCaps.length; j++) {
            // console.log(matchingCaps[j]);
            await delSharing(matchingCaps[j].userCapId);
          }
        }
      }
      refreshUsers();
    }

    // Refresh the users list
    loadPotentialUsers();
  }

  const handleBuilderStatusChange = async (user, newStatus) => {
    const currentStatus = user.builderStatus;
    const statusMap = {
      'ADMIN': 'administrator',
      'HIDDEN': 'hidden',
      null: 'normal'
    };
    
    const action = newStatus === currentStatus ? 'remove' : 'set';
    const statusText = statusMap[newStatus] || 'normal';

    if (window.confirm(`Are you sure you want to ${action} ${user.email} as ${statusText}?`)) {
      await patchUser(user.userId, { 
        builderStatus: newStatus, 
        builderId: newStatus === 'ADMIN' ? builder.builderId : null
      });
      
      // Update the users list
      const updatedUsers = await getUsersByBuilder(builder.builderId);
      setUsers(updatedUsers);
      
      // Update the allSharedUsers list
      const updatedSharedUsers = await getUsersBySharedOnCapsByBuilder(builder.builderId);
      let tempAllSharedUsers = [...updatedSharedUsers];
      for (var i = 0; i < tempAllSharedUsers.length; i++) {
        const oneMonthAgo = new Date(Date.now() - 1000 * 60 * 60 * 24 * 30);
        const actions = await getUserActions(tempAllSharedUsers[i].userId, oneMonthAgo);
        tempAllSharedUsers[i].lastLogin = actions.length > 0 ? actions[0].actionDate : null;
        // Update the builderStatus and builderId for the modified user
        if (tempAllSharedUsers[i].userId === user.userId) {
          tempAllSharedUsers[i].builderStatus = newStatus;
          tempAllSharedUsers[i].builderId = newStatus === 'ADMIN' ? builder.builderId : null;
        }
      }
      setAllSharedUsers(tempAllSharedUsers);
      
      // Update the current user with the new status
      const updatedUser = { 
        ...user, 
        builderStatus: newStatus,
        builderId: newStatus === 'ADMIN' ? builder.builderId : null
      };
      setCurrentUser(updatedUser);
    }
  };

  const refreshUsers = async () => {
    try {
      // Refresh the users list
      const updatedUsers = await getUsersByBuilder(builder.builderId);
      setUsers(updatedUsers);
      
      // Refresh the shared users list
      const updatedSharedUsers = await getUsersBySharedOnCapsByBuilder(builder.builderId);
      let tempAllSharedUsers = [...updatedSharedUsers];
      for (var i = 0; i < tempAllSharedUsers.length; i++) {
        const oneMonthAgo = new Date(Date.now() - 1000 * 60 * 60 * 24 * 30);
        const actions = await getUserActions(tempAllSharedUsers[i].userId, oneMonthAgo);
        tempAllSharedUsers[i].lastLogin = actions.length > 0 ? actions[0].actionDate : null;
      }
      setAllSharedUsers(tempAllSharedUsers);
      
      // Refresh potential users
      loadPotentialUsers();
    } catch (error) {
      console.error('Error refreshing users:', error);
    }
  };

  // if (getUser().role !== 'ADMIN') return <div>Access Denied</div>;

  return (
    <div className="access-control-page">
      {getUser().role==='ADMIN' && (
        <div>
          <Table>
            <thead>
              <tr>
                <th style={{ fontSize: isMobile ? '12px' : 'inherit' }}>View as (BEDROCK):</th>
                <th>
                  <DropdownButton 
                    title={currentUser.email} 
                    onSelect={(newUser) => setCurrentUser(lookupUser(newUser))}
                    id="admin-simulate-user"
                    style={{ fontSize: isMobile ? '12px' : 'inherit' }}
                  >
                    {users.map(user => (
                      <MenuItem key={user.userId+'-option'} eventKey={user.userId}>{user.email}</MenuItem>
                    ))}
                  </DropdownButton>
                </th>
                {!isMobile && (
                  <th>
                    <CopyToClipboard text={builder.builderId} onCopy={onCopy}>
                      <Button bsSize="small" style={{ marginLeft: '10px' }}>
                        {copied ? 'Copied!' : 'Copy Builder ID'}
                      </Button>
                    </CopyToClipboard>
                  </th>
                )}
              </tr>
              <tr>
                <th rowSpan={2}>
                  <ButtonGroup>
                    <Button 
                      bsSize={isMobile ? 'small' : null} 
                      disabled={builder.stripeId === null}
                      onClick={async () => {
                          try {
                            const response = await createCustomerPortal(currentUser.userId);
                            window.open(response.url, '_blank');
                          } catch (error) {
                            console.error('Error opening billing portal:', error);
                          }
                        }}>
                      Billing Dashboard
                    </Button>
                    <Button bsSize={isMobile ? 'small' : null} onClick={() => {props.history.push(`/cameramanagement/${builder.builderId}`)}}>
                      Camera Management
                    </Button>
                  </ButtonGroup>
                </th>
              </tr>
            </thead>
          </Table>
        </div>
      )}
      <PageHeader>
        {builder && builder.name && builder.name+" Admin Dashboard"}
      </PageHeader>
      {currentUser && currentUser.builderStatus==='ADMIN' ? (
        <>
          <h4 className="shared-users-header">
            <span>Employee Settings</span>
            <div className="header-buttons">
              <Button 
                bsStyle="success"
                onClick={() => setShowAddUserModal(true)}
                className="add-user-button"
              >
                <i className="fas fa-plus-circle" /> Add New User
              </Button>
              <Button 
                bsStyle="default" 
                className="help-button"
                onClick={() => setShowAccessInfo(!showAccessInfo)}
              >
                <i className="fas fa-question-circle"></i> Help
              </Button>
            </div>
          </h4>
          {showAccessInfo && (
            <AccessLevelDescriptions isMobile={isMobile} showExtraInfo={true} />
          )}
          <UserTable 
            users={users}
            caps={caps}
            userCaps={userCaps}
            currentUser={currentUser}
            isMobile={isMobile}
            handleUserClick={handleUserClick}
            handleAccessChange={handleAccessChange}
            getAccessColor={getAccessColor}
            getReadableAccessLevel={getReadableAccessLevel}
          />

          <AddUserModal
            show={showAddUserModal}
            onHide={() => setShowAddUserModal(false)}
            caps={caps}
            userCaps={userCaps}
            currentUser={currentUser}
            isMobile={isMobile}
            getAccessColor={getAccessColor}
            getReadableAccessLevel={getReadableAccessLevel}
            onUsersAdded={refreshUsers}
          />

          <div>
            <h4 className="shared-users-header">
              {moreUsers.length > 0 ? "Other Users with Camera Access" : ""}
            </h4>
            <SharedUsersTable 
              moreUsers={moreUsers}
              handleUserClick={handleUserClick}
              handleAddUserToBuilder={handleAddUserToBuilder}
              removeAllBuilderAccess={removeAllBuilderAccess}
              isMobile={isMobile}
              caps={caps}
              loadingMoreUsers={loadingMoreUsers}
            />
          </div>
          
          {getUser().role === 'ADMIN' && potentialUsers.length > 0 && (
            <div>
              <h4 style={{ marginTop: '30px', marginBottom: '15px', color: '#666' }}>
                {potentialUsers && potentialUsers.length > 0 && "Other users from " + currentUser?.email?.split('@')[1] + " (BEDROCK ONLY)"}
              </h4>
            <PotentialUsersTable 
              potentialUsers={potentialUsers}
              loadingPotentialUsers={loadingPotentialUsers}
              caps={caps}
              handleAddUserToBuilder={handleAddUserToBuilder}
              isMobile={isMobile}
              />
            </div>
          )}
        </>
      ) : getUser().role==='ADMIN' ? (
        <>
          <div style={{ textAlign: 'center', marginTop: '50px', fontSize: '18px', color: '#666' }}>
            You are not authorized to view this page
          </div>
          <div>
            <h4 style={{ marginTop: '30px', marginBottom: '15px', color: '#666' }}>
              Set the Builder Administrator (BEDROCK ONLY)
            </h4>
            <Table bordered condensed hover>
              <thead>
                <tr>
                  <th>Email</th>
                  <th>Name</th>
                  <th>Last Login</th>
                  <th>Action</th>
                </tr>
              </thead>
              <tbody>
                {allSharedUsers.map(user => (
                  <tr key={user.userId} style={{ 
                    backgroundColor: user.builderId === builder.builderId ? '#e8f5e9' : 'inherit'
                  }}>
                    <td>{user.email}</td>
                    <td>
                      {user.firstName ? 
                        `${toProperCase(user.firstName)} ${user.lastName ? toProperCase(user.lastName) : ''}` : 
                        'Not yet logged in'}
                    </td>
                    <td>
                      {user.lastLogin ? (
                        <div>
                          <div>{new Date(user.lastLogin).toLocaleDateString()}</div>
                          <div style={{ fontSize: '0.9em', color: '#666' }}>
                            {new Date(user.lastLogin).toLocaleTimeString()}
                          </div>
                        </div>
                      ) : 'More than a month ago'}
                    </td>
                    <td style={{ textAlign: 'center' }}>
                      <Button 
                        bsSize="sm"
                        bsStyle={user.builderStatus === 'ADMIN' ? 'success' : 'default'}
                        onClick={() => handleBuilderStatusChange(user, user.builderStatus === 'ADMIN' ? null : 'ADMIN')}
                      >
                        {user.builderStatus === 'ADMIN' ? 'Current Admin' : 'Set as Admin'}
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </div>
        </>
      ) : (
        <div style={{ textAlign: 'center', marginTop: '50px', fontSize: '18px', color: '#666' }}>
          You are not authorized to view this page
        </div>
      )}
    </div>
  );
}